import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ImgOptimized from "./ImgOptimized";
import { getUpdatedImageUrlWithSize } from "../utils/helpers";

const FullPageImageSlider = ({
  images = [],
  isOpen = false,
  onClose,
  initialIndex = 0,
  isMaterial = false, // to show vertical dock
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const thumbnailRef = useRef(null);
  let pallateImgs = isMaterial
    ? [...images].filter((img, idx) => idx % 2 == 1)
    : [];
  var images = isMaterial
    ? [...images].filter((img, idx) => idx % 2 == 0)
    : images;
  console.log({ images, pallateImgs, isMaterial });

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (thumbnailRef.current && images.length > 0) {
      const thumbnailContainer = thumbnailRef.current;
      const thumbnailWidth = 64;
      const gap = 8;
      const containerWidth = thumbnailContainer.offsetWidth;

      const scrollPosition =
        (thumbnailWidth + gap) * currentIndex -
        containerWidth / 2 +
        thumbnailWidth / 2;

      thumbnailContainer.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: "smooth",
      });
    }
  }, [currentIndex, images.length]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case "Escape":
          onClose?.();
          break;
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [isOpen, currentIndex]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const getImageUrl = (image, size) => {
    if (typeof image === "string") {
      return getUpdatedImageUrlWithSize(
        image.startsWith("http")
          ? image
          : `${process.env.REACT_APP_BACK_URL}/api/minio/${image}`,
        size == "large" ? [2, 2, 3] : [1, 1, 1],
      );
    }
    return image?.url || image?.src || "";
  };

  if (!isOpen || images.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-[100]">
      {/* Header Controls - Fixed at top */}
      <div className="absolute top-0 left-0 right-0 z-[70] bg-gradient-to-b from-black/40 to-transparent p-4">
        <div className="flex justify-between items-center">
          <div className="text-white text-sm md:text-base bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg font-medium">
            {currentIndex + 1} / {images.length}
          </div>

          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors duration-200 p-2 md:p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30"
          >
            <svg
              className="w-5 h-5 md:w-6 md:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Main Image Container - Properly constrained */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="relative w-full h-full flex items-center justify-center px-20"
          style={{
            paddingTop: "80px", // Space for header
            paddingBottom: "120px", // Space for dock
          }}
        >
          {/* Main Image with fixed constraints */}
          <ImgOptimized
            src={getImageUrl(images[currentIndex], "large")}
            alt={`Image ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl z-10"
            style={{
              maxHeight: "calc(100dvh - 200px)",
              maxWidth: "calc(100dvw - 160px)",
            }}
            onError={(e) => {
              console.error(`Failed to load image at index ${currentIndex}`);
              e.target.style.opacity = "0.3";
            }}
          />
        </div>
      </div>

      {/* Navigation Buttons - Always centered, high z-index */}
      {images.length > 1 && !isMaterial && (
        <>
          <button
            onClick={goToPrevious}
            className="fixed left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-all duration-200 p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 hover:scale-110 z-[60]"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="fixed right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-all duration-200 p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 hover:scale-110 z-[60]"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </>
      )}

      {/* Right Side Material Dock - Vertical layout */}
      {isMaterial && pallateImgs.length > 0 && (
        <div className="fixed right-8 top-0 h-full z-[65] flex flex-col gap-4 p-4 bg-gradient-to-l from-black/50 to-transparent">
          {/* Palette Images - Odd Index Section */}
          <div className="flex-1 overflow-y-auto flex flex-col gap-2 bg-white/10 backdrop-blur-md p-2 rounded-lg">
            <div className="text-white text-xs font-semibold px-2 py-1">
              Palette
            </div>
            {pallateImgs.map((image, index) => {
              // Map back to original index in the full images array
              const originalIndex = index;
              return (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(originalIndex)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 hover:scale-105 ${
                    currentIndex === originalIndex
                      ? "border-white scale-110 shadow-lg ring-2 ring-white/40"
                      : "border-white/20 hover:border-white/60"
                  }`}
                >
                  <ImgOptimized
                    src={getImageUrl(image, "small")}
                    alt={`Palette ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      if (e && e?.target) {
                        e.target.style.opacity = "0.3";
                      }
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom Dock - Fixed at bottom with high z-index */}
      <div className="absolute bottom-0 left-0 right-0 z-[70] bg-gradient-to-t from-black/50 to-transparent pt-6 pb-4">
        <div className="flex justify-center">
          {/* Mobile: Dot Indicators */}
          <div className="md:hidden">
            <div className="flex gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              {images.slice(0, 10).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 flex-shrink-0 ${
                    index === currentIndex
                      ? "bg-white scale-125"
                      : "bg-white/50"
                  }`}
                />
              ))}
              {images.length > 10 && (
                <span className="text-white text-xs ml-2">
                  +{images.length - 10}
                </span>
              )}
            </div>
          </div>

          {/* Desktop: Scrollable Thumbnail Strip */}
          <div className="hidden md:block max-w-4xl w-full px-4">
            <div
              ref={thumbnailRef}
              className="flex gap-2 bg-white/25 backdrop-blur-md p-3 rounded-xl overflow-x-auto"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 hover:scale-105 ${
                    index === currentIndex
                      ? "border-white scale-110 shadow-lg ring-2 ring-white/40"
                      : "border-white/20 hover:border-white/60"
                  }`}
                >
                  <ImgOptimized
                    src={getImageUrl(image, "small")}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.opacity = "0.3";
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Background Click to Close */}
      <div className="absolute inset-0 z-0" onClick={onClose} />
    </div>,
    document.body,
  );
};

export default FullPageImageSlider;
